/**
 * UX-1 — the pure decisions behind the shared «کارهای امروز» worklist strip, as ONE tested authority (the
 * React component <AttentionStrip> and its module-level fetch store consume these; the DOM render is left to
 * CI + the browser pass). Two of these are genuinely load-bearing and were the whole point of UX-1:
 *
 *   1. `attentionCacheFresh` — the single 30s TTL rule that makes deals↔cash reuse ONE /api/attention scan
 *      instead of N. This constant + rule is single-sourced here so the store can't drift from what's tested.
 *   2. `attentionDefaultOpen` — the collapsible variant opens itself ONLY on a critical day, so a calm day
 *      never shoves the deals / cash screen down; once the user toggles, their choice wins (that override is
 *      the component's, not this pure rule's).
 *
 * `attentionHeading` and `dueLabel` are the display helpers, pinned so the Persian copy can't silently break.
 */

export const ATTENTION_TTL_MS = 30_000;

/** True while a cached scan is still within the shared-fetch window — the ONE rule that dedupes the multi-scan
 *  /api/attention across the dashboard, deals, and cash strips. */
export function attentionCacheFresh(
  fetchedAt: number,
  now: number,
  ttl: number = ATTENTION_TTL_MS,
): boolean {
  return now - fetchedAt < ttl;
}

/** How many of today's items are `critical` — drives both the heading count and the disclosure default. */
export function criticalCount(items: readonly { severity: string }[]): number {
  return items.filter((a) => a.severity === 'critical').length;
}

/** The collapsible strip's default open state: open only when something is critical today (else folded, so a
 *  calm day adds nothing visible to the deals / cash landing). The user's manual toggle supersedes this. */
export function attentionDefaultOpen(items: readonly { severity: string }[]): boolean {
  return criticalCount(items) > 0;
}

/** «کارهای امروز (N بحرانی)» when anything is critical, else the plain total. Only shown for a non-empty list
 *  (the strip returns null when empty), so this never renders a zero. */
export function attentionHeading(items: readonly { severity: string }[]): string {
  const c = criticalCount(items);
  return `کارهای امروز (${c ? `${c} بحرانی` : items.length})`;
}

/** Overdue vs. remaining vs. no-due label for a row; null when the item carries no due date. */
export function dueLabel(dueDays: number | null): string | null {
  if (dueDays == null) return null;
  return dueDays < 0 ? `${-dueDays} روز گذشته` : `${dueDays} روز مانده`;
}
